import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs';
import { fromInstant, nowInPreferenceTz } from 'utils/timezone';
import _ from 'lodash';
import Snackbar from '@mui/material/Snackbar';
import { connect } from 'react-redux';

import localeMessage from '../localeMessage';

const bodyStyle = {
  backgroundColor: '#19336a',
  boxShadow: '0 0 50px 0 rgba(40, 71, 140, 0.2)',
  borderTopLeftRadius: '20px',
  borderTopRightRadius: '20px',
};

const contentStyle = {
  color: '#ffffff',
  fontFamily: 'Barlow',
  fontSize: '24px',
  fontWeight: 'bold',
  letterSpacing: '0.24px',
  paddingLeft: '20px',
  paddingRight: '20px',
};

function Countdown({ appointment, sessionOver, connected, locale }) {
  const [countdown, setCountdown] = useState('Loading...');
  const [timer, setTimer] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const intervalID = useRef(null);
  const propsRef = useRef({ appointment, sessionOver });
  propsRef.current = { appointment, sessionOver };

  useEffect(() => {
    const now = fromInstant(new Date());
    const end = dayjs.utc(_.get(appointment, 'end_date'));
    const ms = end.diff(now);

    let newTime = ms;
    const interval = 1000;
    const oneHour = 3600000;
    const endTime = fromInstant(_.get(appointment, 'end_date'));
    intervalID.current = setInterval(() => {
      newTime -= interval;
      const d = dayjs.duration(newTime);
      const s = Math.floor(d.asHours()) + dayjs.utc(newTime).format(':mm');

      setCountdown(s);

      // When the session has expired, user can still remain in the session
      if (newTime < 0) {
        const timeOver = nowInPreferenceTz().diff(endTime, 'minutes');
        const negativeRemainder =
          timeOver >= 10 ? `-0:${timeOver}` : `-0:0${timeOver}`;
        setSnackbarOpen(true);
        setCountdown(negativeRemainder);
        propsRef.current.sessionOver();
      }
      // Kick users out if they remain after the session has been expired for an hour
      if (newTime < -oneHour) {
        clearInterval(intervalID.current);
      }
    }, interval);

    return () => {
      clearInterval(intervalID.current);
    };
    // Match class componentDidMount: run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTimer = () => {
    setTimer(prev => !prev);
  };

  return (
    <div className="bottom-bar-timer">
      {timer ? (
        <p className={connected ? 'connected-time' : 'time'}>
          {countdown}
        </p>
      ) : (
        <p className="time">- -:- -</p>
      )}
      <p role="presentation" className="hide-time" onClick={toggleTimer}>
        {timer
          ? localeMessage('hideTime', locale)
          : localeMessage('showTime', locale)}
      </p>
      <Snackbar
        ContentProps={{ style: bodyStyle }}
        className="bottom-bar-snack"
        onClose={() => false}
        open={snackbarOpen}
        message={
          <span style={contentStyle}>
            {localeMessage('hasExpired', locale)}
          </span>
        }
      />
    </div>
  );
}

Countdown.propTypes = {
  appointment: PropTypes.object.isRequired,
  sessionOver: PropTypes.func.isRequired,
  connected: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  return {
    locale: (state.user && state.user.locale) || 'en',
  };
}

export default connect(mapStateToProps)(Countdown);
